import React from "react";
import { motion, useInView } from "framer-motion";
import { FaMicrophone, FaMagic, FaFileAlt, FaShoppingCart } from "react-icons/fa";
import { EasyonReveal } from "./EasyonMotion";

const aiFeatures = [
    { icon: FaMicrophone, title: "Voice Commands", desc: "Speak naturally to create documents" },
    { icon: FaMagic, title: "Smart Drafts", desc: "AI pre-fills items, rates, and terms" },
];

export const EasyonAiShowcaseSection = () => (
    <section className="eo-ai eo-ai--primary-showcase">
        <div className="eo-container">
            <div className="eo-ai__layout">
                <EasyonReveal className="eo-ai__copy">
                    <span className="eo-eyebrow">Business Assistant</span>
                    <h2 className="eo-h2">Your Intelligent Business Assistant</h2>
                    <p className="eo-lead">
                        Automate quotations, invoices, purchase orders, approvals, and day-to-day operations through one
                        intelligent business assistant designed for growing enterprises.
                    </p>

                    <div className="eo-ai__features">
                        {aiFeatures.map((f) => {
                            const Icon = f.icon;
                            return (
                                <motion.div key={f.title} className="eo-ai__feature" whileHover={{ x: 4 }}>
                                    <div className="eo-ai__feature-icon">
                                        <Icon size={16} />
                                    </div>
                                    <div>
                                        <strong>{f.title}</strong>
                                        <p>{f.desc}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </EasyonReveal>

                <AiTypewriterPanel />
            </div>
        </div>
    </section>
);

const userPrompt = "Create a quotation for ABC Traders with 12 SKUs and GST 18%.";
const assistantReply = "Quotation QT-1048 generated. Total ₹1,24,500. Do you want any plan for increase the quantity?";

const AiTypewriterPanel = () => {
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const inView = useInView(panelRef, { amount: 0.45 });

    const [showUserMessage, setShowUserMessage] = React.useState(false);
    const [aIndex, setAIndex] = React.useState(0);

    React.useEffect(() => {
        let delayTimer: number | undefined;
        let typingTimer: number | undefined;

        if (!inView) {
            setShowUserMessage(false);
            setAIndex(0);
            return;
        }

        setShowUserMessage(true);
        setAIndex(0);

        const typeAssistantReply = (index: number) => {
            typingTimer = window.setTimeout(() => {
                const next = index + 1;
                setAIndex(next);
                if (next < assistantReply.length) {
                    typeAssistantReply(next);
                }
            }, 40);
        };

        delayTimer = window.setTimeout(() => {
            typeAssistantReply(0);
        }, 650);

        return () => {
            if (delayTimer) window.clearTimeout(delayTimer);
            if (typingTimer) window.clearTimeout(typingTimer);
        };
    }, [inView]);

    return (
        <motion.div
            ref={panelRef}
            className="eo-ai-chat-card"
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.7 }}
        >
            <div className="eo-ai-chat-card__head">
                <span>EasyOn AI Console</span>
                <em>Live</em>
            </div>
            <div className="eo-ai-chat-card__body">
                <div className="eo-ai-chat-card__msg eo-ai-chat-card__msg--user">
                    {showUserMessage ? userPrompt : ""}
                </div>
                <div className="eo-ai-chat-card__msg eo-ai-chat-card__msg--assistant">
                    {assistantReply.slice(0, aIndex)}
                    <i className={showUserMessage && aIndex < assistantReply.length ? "cursor" : ""} />
                </div>
            </div>
        </motion.div>
    );
};
